import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Megaphone, Plus, Edit, Search, CalendarClock, Loader2 } from 'lucide-react';
import { format, parseISO, isPast } from 'date-fns';
import { corporateAPI } from '../../api';
import { toast } from 'sonner';

const ROLE_GROUPS = [
  { value: 'board_directors', label: 'Board of Directors' },
  { value: 'officers', label: 'Officers' },
  { value: 'committee_members', label: 'Committee Members' },
  { value: 'shareholders', label: 'Shareholders' },
  { value: 'external_counsel', label: 'External Counsel' },
  { value: 'external_auditors', label: 'External Auditors' },
];

const AnnouncementsTab = ({ announcements, orgUsers, searchTerm, setSearchTerm, fetchData }) => {
  const [annDialog, setAnnDialog] = useState(false);
  const [annEdit, setAnnEdit] = useState(null);
  const [annForm, setAnnForm] = useState({ title: '', message: '', end_date: '', recipient_mode: 'all', groups: [], user_ids: [] });
  const [savingAnn, setSavingAnn] = useState(false);

  const handleSaveAnnouncement = async () => {
    if (!annForm.title) { toast.error('Title is required'); return; }
    setSavingAnn(true);
    try {
      const fd = new FormData();
      fd.append('title', annForm.title);
      fd.append('message', annForm.message || '');
      if (annForm.end_date) fd.append('end_date', annForm.end_date);
      if (annForm.recipient_mode === 'all') {
        fd.append('recipients', 'all');
      } else {
        annForm.groups.forEach(g => fd.append('recipient_groups', g));
        annForm.user_ids.forEach(id => fd.append('recipient_ids', id));
      }
      if (annEdit) fd.append('announcement_id', annEdit.id);
      await corporateAPI.upsertAnnouncement(fd);
      toast.success(annEdit ? 'Announcement updated' : 'Announcement created');
      setAnnDialog(false);
      setAnnEdit(null);
      setAnnForm({ title: '', message: '', end_date: '', recipient_mode: 'all', groups: [], user_ids: [] });
      fetchData();
    } catch { toast.error('Failed to save announcement'); }
    finally { setSavingAnn(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
          <Input placeholder="Search announcements..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 border-[#E2E6EA]" data-testid="search-announcements-hub" />
        </div>
        <Button onClick={() => { setAnnEdit(null); setAnnForm({ title: '', message: '', end_date: '', recipient_mode: 'all', groups: [], user_ids: [] }); setAnnDialog(true); }}
          className="bg-[#00A3A3] hover:bg-[#008585] shrink-0" data-testid="new-announcement-btn">
          <Plus className="h-4 w-4 mr-2" />New Announcement
        </Button>
      </div>
      {announcements.length === 0 ? (
        <Card className="border-[#E2E6EA]"><CardContent className="py-12 text-center">
          <Megaphone className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
          <h3 className="text-base font-medium text-[#1A1D21] mb-1">No announcements yet</h3>
          <p className="text-sm text-[#5A6570]">Create your first announcement to notify stakeholders</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {announcements.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
            .filter(ann => (ann.title || '').toLowerCase().includes(searchTerm.toLowerCase()) || (ann.message || '').toLowerCase().includes(searchTerm.toLowerCase()))
            .map(ann => {
            const expired = ann.end_date && isPast(parseISO(ann.end_date));
            const isAll = ann.recipients?.includes('all') || (!ann.recipient_groups?.length && !ann.recipient_ids?.length);
            return (
              <Card key={ann.id} className={`border-[#E2E6EA] ${expired ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-[#1A1D21]">{ann.title}</h3>
                        {isAll && <Badge className="text-[10px] bg-[#00A3A3]/10 text-[#00A3A3]">Portal</Badge>}
                        {expired && <Badge variant="outline" className="text-[10px] text-red-500 border-red-200">Expired</Badge>}
                      </div>
                      {ann.message && <p className="text-xs text-[#5A6570] mt-1 line-clamp-2">{ann.message}</p>}
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-[10px] text-[#A8B1B9]">
                        <span>{new Date(ann.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        {ann.end_date && <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" />Expires: {format(parseISO(ann.end_date), 'MMM d, yyyy')}</span>}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-[#00A3A3]"
                      onClick={() => {
                        setAnnEdit(ann); setAnnForm({ title: ann.title, message: ann.message || '', end_date: ann.end_date || '',
                          recipient_mode: isAll ? 'all' : 'targeted', groups: ann.recipient_groups || [], user_ids: ann.recipient_ids || [] });
                        setAnnDialog(true);
                      }}><Edit className="h-3 w-3 mr-1" />Edit</Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Announcement Dialog */}
      <Dialog open={annDialog} onOpenChange={setAnnDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{annEdit ? 'Edit Announcement' : 'New Announcement'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Title *</Label><Input value={annForm.title} onChange={e => setAnnForm({ ...annForm, title: e.target.value })} placeholder="Announcement title..." className="mt-1 border-[#E2E6EA]" data-testid="ann-title-input" /></div>
            <div><Label>Message</Label><Textarea value={annForm.message} onChange={e => setAnnForm({ ...annForm, message: e.target.value })} placeholder="Announcement details..." className="mt-1 border-[#E2E6EA] min-h-[80px]" data-testid="ann-message-input" /></div>
            <div><Label>End Date (Auto-expire)</Label><Input type="date" value={annForm.end_date} onChange={e => setAnnForm({ ...annForm, end_date: e.target.value })} className="mt-1 border-[#E2E6EA]" /></div>
            <div>
              <Label>Recipients</Label>
              <Select value={annForm.recipient_mode} onValueChange={v => setAnnForm({ ...annForm, recipient_mode: v })}>
                <SelectTrigger className="mt-1 border-[#E2E6EA]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All (Posts to Stakeholders Portal)</SelectItem>
                  <SelectItem value="targeted">Targeted Groups / Individuals</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {annForm.recipient_mode === 'targeted' && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Groups</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {ROLE_GROUPS.map(g => (
                      <label key={g.value} className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <Checkbox checked={annForm.groups.includes(g.value)} onCheckedChange={c => setAnnForm(f => ({ ...f, groups: c ? [...f.groups, g.value] : f.groups.filter(x => x !== g.value) }))} className="h-3.5 w-3.5" />
                        {g.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Individual Users</Label>
                  <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto">
                    {orgUsers.map(u => (
                      <label key={u.id} className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <Checkbox checked={annForm.user_ids.includes(u.id)} onCheckedChange={c => setAnnForm(f => ({ ...f, user_ids: c ? [...f.user_ids, u.id] : f.user_ids.filter(x => x !== u.id) }))} className="h-3.5 w-3.5" />
                        {u.full_name}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveAnnouncement} disabled={savingAnn} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="save-announcement-btn">
              {savingAnn ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{annEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnnouncementsTab;
